import React, { ReactNode } from 'react';
import {
  IconButton as MuiIconButton,
  IconButtonProps as MuiIconButtonProps,
  makeStyles,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { omit } from 'lodash-es';

type IconButtonVariant = 'primary' | 'outlined' | 'close' | 'basic';

interface IconComponent {
  icon?: ReactNode;
  variant: IconButtonVariant;
}

export type IconButtonProps<T extends React.ElementType> = Omit<MuiIconButtonProps<T>, 'variant'> &
  IconComponent;

export function IconButton<T extends React.ElementType = 'button'>({
  variant,
  icon,
  className,
  ...props
}: IconButtonProps<T>): JSX.Element {
  const classes = useIconButtonStyles();
  const cleanedProps = omit(props, ['disableElevation', 'fullWidth', 'icon', 'variant']);

  return (
    <MuiIconButton {...cleanedProps} className={`${classes[variant]} ${className}`}>
      {cleanedProps.children || icon}
    </MuiIconButton>
  );
}

export const CloseIconButton: React.FC<Omit<IconButtonProps<'button'>, 'variant' | 'icon'>> = (
  props,
) => <IconButton variant={'close'} aria-label='close' icon={<Close />} {...props} />;

const useIconButtonStyles = makeStyles((theme) => ({
  basic: {
    backgroundColor: 'transparent',
    '& svg': {
      fontSize: 32,
    },
  },
  close: {
    position: 'absolute',
    top: 0,
    right: 0,
    '& svg': {
      fontSize: 24,
    },
  },
  primary: {
    padding: 8,
    color: theme.palette.primary.contrastText,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 0,
    '&:hover': {
      backgroundColor: '#FF964D',
      border: '1px solid #FF964D',
      color: theme.palette.primary.contrastText,
    },
    '&:disabled': {
      borderWidth: 0,
      backgroundColor: theme.palette.action.disabledBackground,
      color: theme.palette.common.white,
    },
  },
  outlined: {
    color: theme.palette.primary.main,
    padding: 8,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: 'transparent',
    borderRadius: 0,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
    '&:disabled': {
      borderColor: theme.palette.action.disabled,
      color: theme.palette.action.active,
    },
  },
}));
